import {Interceptor} from '@jobseeker/common-core/lib/components/Inteceptor';
import {
  getConfigByApp,
  getDeviceOs,
  utilServerSideDeviceDetection,
} from '@utils';
import {metaInfo} from '@configs';
import {COURSE, HOME} from '@constants';
import {wrapper} from '@jobseeker/common-core/lib/stores';
import {useDispatch} from 'react-redux';
import useValidateAuth from '@jobseeker/common-core/lib/hooks/useValidateAuth';
import {BaseContext} from 'next/dist/shared/lib/utils';
import {Chat, CourseDetailComponent} from '@components';
import {
  courseService,
  gladiatorService,
} from '@jobseeker/common-core/lib/services';
import {COOKIE_LOGIN_AUTH} from '@jobseeker/common-core/lib/constants';
import cookie from 'cookie';
import {Course} from '@jobseeker/common-core/lib/types';
import {useEffect, useRef} from 'react';
import {
  handleTracking,
  InitiateUBA,
  TrackEventUBA,
} from '@jobseeker/common-core/lib/utils';
import {useRouter} from 'next/router';
import {loadSEO} from '@helpers';
import {cleanUrl} from '@jobseeker/lotus/lib/utils';
import {SeoLayout} from '@layouts';

type CourseDetailProps = {
  coursePublishedId: number;
  courseDetail: Course;
  userId: number;
  token: string;
  seoConfig: object;
};

const CourseDetail = ({
  coursePublishedId,
  courseDetail,
  userId,
  token,
  seoConfig,
}: CourseDetailProps) => {
  const metaData = getConfigByApp(metaInfo, HOME, true, COURSE);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  useValidateAuth(useDispatch<any>());

  const router = useRouter();

  const hasTrackedUBA = useRef(false);

  useEffect(() => {
    let userAgent = '';
    const refOrg = router.query.reforg || '';
    const refInv = router.query.refinv || '';

    if (typeof navigator !== 'undefined') {
      userAgent = navigator.userAgent || navigator.vendor || '';
    }

    handleTracking('lgViewCourse', {
      event_category: 'Learn and Grow',
      CourseId: `${coursePublishedId}`,
      UserId: `${userId}`,
      Status: `${userId ? 'LoggedIn' : 'LoggedOut'}`,
      Device: `${getDeviceOs(userAgent)}`,
      Reforg: refOrg.toString(),
      RefInv: refInv.toString(),
    });

    InitiateUBA('Course Detail Page');

    if (!hasTrackedUBA.current) {
      const sessionUserId = sessionStorage.getItem('userId') || userId || '-1';

      hasTrackedUBA.current = true;

      TrackEventUBA('courseDetailPageView', 'view', {
        ref: router.query.ref || '',
        refOrg,
        refInv,
        sessionUsername: sessionUserId,
      });
    }
  }, [userId]);

  return (
    <>
      <Interceptor metaInfo={metaData}>
        <SeoLayout seoConfig={seoConfig} />
        <CourseDetailComponent
          coursePublishedId={coursePublishedId}
          courseDetail={courseDetail}
          userId={userId}
          token={token}
        />
      </Interceptor>
      <Chat />
    </>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  store => async (context: BaseContext) => {
    const {slug = ''} = context.query;
    let coursePublishedId = 0;
    let userId = -1;
    let token = '';
    let courseDetail: Partial<Course> = {};

    try {
      coursePublishedId = parseInt(
        String(slug)
          .replace(/\.html/, '')
          .split('-')
          .pop() ?? '0',
        10
      );

      if (context.req?.headers?.cookie) {
        const cookies = cookie.parse(context.req.headers.cookie);

        if (cookies?.[COOKIE_LOGIN_AUTH]) {
          const validateInfo = await gladiatorService.validate('validate', {
            headers: {
              cookie: context.req?.headers?.cookie,
            },
          });

          userId = parseInt(validateInfo?.userId ?? '-1', 10);
          token = validateInfo?.token ?? '';
        }
      }
    } catch (err) {}

    store.dispatch({
      type: 'AppContext/set_current_page',
      payload: {page: COURSE},
    });

    const {isMobile} = utilServerSideDeviceDetection(context);

    if (isMobile) {
      store.dispatch({
        type: 'AppContext/set_layout_device',
        payload: 'mobile',
      });
    }

    if (coursePublishedId > 0) {
      courseDetail =
        (
          await courseService.getCourseDetails({
            coursePublishedId,
            userId,
            token,
            headers: {
              Origin: process.env.NEXT_PUBLIC_LOAD_CONFIG,
              Referer: process.env.NEXT_PUBLIC_LOAD_CONFIG,
            },
          })
        )?.data?.[0] ?? {};
    }

    const courseSEOMetadata = {
      title: '',
      description: '',
      slugUrl: '',
    };

    if (courseDetail) {
      courseSEOMetadata.title = courseDetail?.title ?? '';
      courseSEOMetadata.description = courseDetail?.oneLiner ?? '';
      courseSEOMetadata.slugUrl = `/${cleanUrl(
        courseSEOMetadata.title
      )}-${courseDetail?.id}`;
    }

    const seoConfig = await loadSEO(
      COURSE,
      {
        title: courseSEOMetadata.title,
        description: courseSEOMetadata.description,
      },
      courseSEOMetadata.slugUrl,
      true
    );

    return {
      props: {
        coursePublishedId,
        courseDetail: courseDetail ?? {},
        seoConfig,
        userId,
        token,
      },
    };
  }
);

export default CourseDetail;
